import { ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatCount } from '@/utils/number';
import { LATEST_START_BADGE_CLASS } from '../constants/moTravel.constants';
import { formatShortDay } from '../utils/dayLabels';
import { describeStartPlace, type StartPlace } from '../utils/startPlaces';
import { MoStartIcon } from './MoStartIcon';

interface MoDockedStartPlacePickerProps {
  startPlaces: StartPlace[];
  onStartSelect: (startKey: string) => void;
}

export function MoDockedStartPlacePicker({
  startPlaces,
  onStartSelect,
}: MoDockedStartPlacePickerProps) {
  const [latestPlace, ...olderPlaces] = startPlaces;
  if (!latestPlace) return null;

  return (
    <div role="group" aria-label="Start points" className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onStartSelect(latestPlace.startKey)}
        aria-label={describeStartPlace(latestPlace)}
        className="flex h-10 min-w-0 items-center gap-2 rounded-lg border border-route-start/40 bg-route-start/10 pr-2.5 pl-1.5 text-sm font-medium transition-colors focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none active:bg-route-start/20"
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-route-start text-white shadow-sm">
          <MoStartIcon className="size-3.5" />
        </span>
        <span className="min-w-0 truncate">{latestPlace.title}</span>
        <span className={LATEST_START_BADGE_CLASS}>Latest</span>
      </button>

      {olderPlaces.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="outline"
                className="group h-10 shrink-0 gap-1.5 rounded-lg border-route-start/30 bg-background pr-2 pl-1.5 text-sm font-medium"
                aria-label={formatCount(olderPlaces.length, 'other start point')}
              >
                <span className="flex size-7 items-center justify-center rounded-md bg-route-start/10 text-route-start">
                  <MoStartIcon className="size-3.5" />
                </span>
                <span className="tabular-nums">+{olderPlaces.length}</span>
                <ChevronUp
                  className="size-4 text-muted-foreground transition-transform group-data-popup-open:rotate-180"
                  aria-hidden
                />
              </Button>
            }
          />
          <DropdownMenuContent side="top" align="end" sideOffset={8} className="w-64 shadow-lg">
            <DropdownMenuGroup>
              <DropdownMenuLabel className="px-2 pt-1.5 text-2xs font-semibold tracking-wide uppercase">
                Earlier start points
              </DropdownMenuLabel>
              {olderPlaces.map((startPlace) => (
                <DropdownMenuItem
                  key={startPlace.startKey}
                  onClick={() => onStartSelect(startPlace.startKey)}
                  aria-label={describeStartPlace(startPlace)}
                  className="h-11 gap-2.5 px-2"
                >
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-route-start/10">
                    <MoStartIcon className="size-3.5 text-route-start" />
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium">{startPlace.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatShortDay(startPlace.latestDate)}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
